import ContestPageContestViewHeader
  from "components/contests/contest-details/header/contest-page-contest-view-header.tsx";
import React, { useState } from "react";
import styles from "components/contests/contest-details/styles.module.css";
import { ContestItem, } from "components/contests/contest-display-items";
import { Contest, ContestEntry, isBasicContest, isPickPerPoolContest, remainingPicksForEntry } from "interfaces/leaderboard/contest.ts";
import { CONTEST_ENTRY_STATUS, sparketBlack, sparketGold, } from "utils/constants.ts";
import ViewLeaderboardButton from "./view-leaderboard-button.tsx";
import { useAppDispatch, useAppSelector } from "state/hooks.ts";
import BlockyBoldText from "components/custom-texts/blocky-bold-text.tsx";
import { showSignIn } from "state/slices/login-modals-slice.ts";
import { useAuth } from "hooks/auth.tsx";
import { getGroupState, requestToJoinGroup } from "state/slices/group-slice.ts";
import { UserGroupMembershipStatus } from "utils/userGroupMembershipStatus.ts";
import JoinGroupModal from "home/join-group-button/join-group-modal";
import { checkIfUserCanEnterContest } from "utils/contestEntryUtils";
import EnterContestButton from "components/contests/contest-display-items/enter-contest-button.tsx";
import { getContestEntryById, getCurrentEntryIdByContestId } from "state/slices/contests-slice.ts";
import { getContestEntryFee } from "utils/contest-group-utils.ts";
import { isIntegrationOnlyGroup } from "utils/integration-only-groups.ts";
import { getIntegratedAppState } from "state/slices/integrated-app-slice.ts";

interface Props {
  contest: Contest;
  userContestEntries: ContestEntry[];
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const ContestPageContestView = ({
                                  contest,
                                  userContestEntries,
                                  setShowModal,
                                }: Props) => {
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const group = useAppSelector(getGroupState);

  const currentContestEntryId = useAppSelector(state => getCurrentEntryIdByContestId(state, contest.id));
  const currentContestEntry = useAppSelector(state => getContestEntryById(state, currentContestEntryId));
  const isEntryValid = currentContestEntry?.status === CONTEST_ENTRY_STATUS.VALID;
  const remainingUniquePicks = currentContestEntry
    ? remainingPicksForEntry(currentContestEntry)
    : undefined;

  const [showJoinGroupModal, setShowJoinGroupModal] = useState(false);

  const integratedAppState = useAppSelector(getIntegratedAppState);
  const isRestricted =
    isIntegrationOnlyGroup(group.uri) && !integratedAppState.isIntegratedApp;

  const canEnterContest =
    !isRestricted && checkIfUserCanEnterContest(contest, userContestEntries);

  const showLeaderboardButton = currentContestEntryId !== undefined;

  const onEnterContestButtonClick = () => {
    if (!auth.signedIn) {
      dispatch(showSignIn());
      return;
    }

    if (group.name && !(group.status === UserGroupMembershipStatus.APPROVED)) {
      setShowJoinGroupModal(true);
      return;
    }

    setShowModal(true);
  };

  const resolveContestEntryStatusInfoMessage = () => {
    if (!currentContestEntry || remainingUniquePicks === undefined || remainingUniquePicks <= 0) {
      return "";
    }
    if (isPickPerPoolContest(contest)) {
      return remainingUniquePicks === 1
        ? "Make a pick on 1 more pool to enter!"
        : `Make a pick on ${remainingUniquePicks} more pools to enter!`;
    }
    const atLeast = isBasicContest(contest) ? "" : "at least ";
    if (remainingUniquePicks === 1) {
      return `Make ${atLeast}1 more pick with a different contestant to enter!`;
    }
    return `Make ${atLeast}${remainingUniquePicks} picks with different contestants to enter!`;
  };

  return (
    <>
      <div style={{ backgroundColor: sparketBlack }}>
        <ContestPageContestViewHeader contestId={contest.id} userContestEntries={userContestEntries} />

        <div className={styles.wrapper}>
          <ContestItem
            contest={contest}
            userContestEntries={userContestEntries}
            showStatus
            entryFeeOverride={getContestEntryFee(contest, group.id)}
          />

          {isRestricted && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                color: "white",
                fontSize: 12,
                padding: "10px",
                textAlign: "center",
              }}
            >
              <BlockyBoldText>
                This contest is only available inside the {group.name} app. Please re-open Sparket from there.
              </BlockyBoldText>
            </div>
          )}

          {showLeaderboardButton && canEnterContest ? (
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "10px"
            }}>
              <div style={{ flex: 1 }}>
                <ViewLeaderboardButton contestId={contest.id} />
              </div>
              <div style={{ flex: 1 }}>
                <EnterContestButton onClick={onEnterContestButtonClick} text="Enter Again" />
              </div>
            </div>
          ) : showLeaderboardButton ? (
            <ViewLeaderboardButton contestId={contest.id} />
          ) : canEnterContest ? (
            <EnterContestButton onClick={onEnterContestButtonClick} />
          ) : null}

          <div>
            {currentContestEntry && !isEntryValid && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  color: sparketGold,
                  fontSize: 12,
                  marginBottom: 10,
                }}
              >
                <BlockyBoldText>
                  {resolveContestEntryStatusInfoMessage()}
                </BlockyBoldText>
              </div>
            )}
          </div>
        </div>
      </div>
      <JoinGroupModal
        acceptAndJoin={() => {
          dispatch(requestToJoinGroup(group.id));
          setShowJoinGroupModal(false);
          if (!currentContestEntryId) {
            setShowModal(true);
          }
        }}
        show={showJoinGroupModal}
        close={() => setShowJoinGroupModal(false)}
      />
    </>
  );
};

export default ContestPageContestView;
